import { Component } from '@theme/component';

/**
 * Rank products attached to selected quiz answers.
 *
 * Products score once per selected answer. Ties are resolved by the first
 * question and product position where the product appeared, then by handle.
 *
 * @param {QuizData} quiz
 * @param {Map<string, Set<string> | string[] | string>} selections
 * @param {number} limit
 * @returns {RankedProduct[]}
 */
export function rankQuizProducts(quiz, selections, limit) {
  /** @type {Map<string, RankedProduct>} */
  const ranked = new Map();

  quiz.questions.forEach((question, questionIndex) => {
    const selection = selections.get(question.handle);
    const selectedAnswerHandles = new Set(
      selection instanceof Set ? selection : Array.isArray(selection) ? selection : selection ? [selection] : []
    );

    question.answers.forEach((answer, answerIndex) => {
      if (!selectedAnswerHandles.has(answer.handle)) return;

      const productsInAnswer = new Set();
      answer.products.forEach((product, productIndex) => {
        const productKey = String(product.id);
        if (productsInAnswer.has(productKey)) return;
        productsInAnswer.add(productKey);

        const existing = ranked.get(productKey);
        if (existing) {
          existing.score += 1;
          return;
        }

        ranked.set(productKey, {
          ...product,
          score: 1,
          firstQuestionIndex: questionIndex,
          firstAnswerIndex: answerIndex,
          firstProductIndex: productIndex,
        });
      });
    });
  });

  return [...ranked.values()]
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.firstQuestionIndex - b.firstQuestionIndex ||
        a.firstAnswerIndex - b.firstAnswerIndex ||
        a.firstProductIndex - b.firstProductIndex ||
        a.handle.localeCompare(b.handle)
    )
    .slice(0, limit);
}

/**
 * Absolute URL for use inside an email. `image_url` returns a protocol-relative CDN
 * path, and `product.url` is root-relative — neither resolves in an inbox.
 *
 * @param {string | undefined} url
 * @returns {string}
 */
function absoluteUrl(url) {
  if (!url) return '';
  if (url.startsWith('//')) return `https:${url}`;
  return new URL(url, window.location.origin).href;
}

/**
 * Tag a product link for the Omnisend picks email so its sales are attributable.
 * Only applied to the emailed URLs — on-site links stay clean.
 *
 * @param {string} url
 * @param {string} quizName
 * @param {string} handle - product handle, so revenue can be split per recommendation
 * @returns {string}
 */
function withQuizUtm(url, quizName, handle) {
  if (!url) return '';
  const tagged = new URL(url);
  tagged.searchParams.set('utm_source', 'omnisend');
  tagged.searchParams.set('utm_medium', 'email');
  tagged.searchParams.set('utm_campaign', `quiz-${(quizName || 'quiz').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`);
  tagged.searchParams.set('utm_content', handle || '');
  return tagged.href;
}

/** Omnisend stores the identified contact in this cookie; its `track` calls key off it. */
function omnisendContactId() {
  return document.cookie.match(/(?:^|;\s*)omnisendContactID=([^;]+)/)?.[1] || '';
}

/**
 * `omnisend.identifyContact` is an un-awaitable network call with no callback
 * (arity 1, no onSuccess hook), so a `track` fired straight after it attaches to
 * whatever contact was identified previously. Wait for the cookie to change instead.
 *
 * @param {string} previousId
 * @param {number} [timeout=5000]
 */
async function waitForOmnisendContact(previousId, timeout = 5000) {
  const deadline = Date.now() + timeout;
  while (Date.now() < deadline) {
    const current = omnisendContactId();
    if (current && current !== previousId) return current;
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
  // Same shopper re-identifying keeps the same id — send anyway rather than drop the event.
  return omnisendContactId();
}

/**
 * @typedef {Object} ProductData
 * @property {string} id
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {string} price
 * @property {string} description
 * @property {string} image
 * @property {string} imageAlt
 */

/**
 * @typedef {Object} QuizAnswer
 * @property {string} handle
 * @property {ProductData[]} products
 */

/**
 * @typedef {Object} QuizQuestion
 * @property {string} handle
 * @property {QuizAnswer[]} answers
 */

/**
 * @typedef {Object} QuizData
 * @property {string} handle
 * @property {QuizQuestion[]} questions
 */

/**
 * @typedef {ProductData & {
 *   score: number,
 *   firstQuestionIndex: number,
 *   firstAnswerIndex: number,
 *   firstProductIndex: number
 * }} RankedProduct
 */

/**
 * @typedef {Object} QuizRefs
 * @property {HTMLElement} introStage
 * @property {HTMLElement} questionStage
 * @property {HTMLElement[]} steps
 * @property {HTMLElement} questionNavigation
 * @property {HTMLButtonElement} backButton
 * @property {HTMLButtonElement} nextButton
 * @property {HTMLElement} progress
 * @property {HTMLElement} progressBar
 * @property {HTMLElement} progressText
 * @property {HTMLElement} emailStage
 * @property {HTMLFormElement} emailForm
 * @property {HTMLInputElement} emailInput
 * @property {HTMLElement} [omnisendEmbed]
 * @property {HTMLElement} resultsStage
 * @property {HTMLElement} resultsGrid
 * @property {HTMLElement[]} productCards
 * @property {HTMLElement} productCardPool
 * @property {HTMLElement} status
 * @property {HTMLScriptElement} quizData
 */

/**
 * @extends {Component<QuizRefs>}
 */
class MetaobjectProductQuizComponent extends Component {
  /** @type {QuizData} */
  #quiz = { handle: '', questions: [] };

  /** @type {Map<string, Set<string>>} */
  #selections = new Map();

  #questionIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  #initialize() {
    try {
      this.#quiz = JSON.parse(this.refs.quizData.textContent || '{}');
    } catch (error) {
      console.error('[metaobject-product-quiz] Invalid quiz data.', error);
      return;
    }

    this.#selections.clear();
    this.#questionIndex = 0;
    this.#showIntro(false);
  }

  startQuiz() {
    if (this.#quiz.questions.length === 0) return;
    this.#showQuestion(0);
  }

  /** @param {Event} event */
  selectAnswer(event) {
    const target = event.target;
    const button = target instanceof Element ? target.closest('button[data-answer-handle]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const questionHandle = button.dataset.questionHandle;
    const answerHandle = button.dataset.answerHandle;
    const questionIndex = Number(button.dataset.questionIndex);
    if (!questionHandle || !answerHandle || Number.isNaN(questionIndex)) return;

    const selectedAnswers = this.#selections.get(questionHandle) || new Set();
    const isSelected = selectedAnswers.has(answerHandle);
    if (isSelected) {
      selectedAnswers.delete(answerHandle);
    } else {
      selectedAnswers.add(answerHandle);
    }

    if (selectedAnswers.size > 0) {
      this.#selections.set(questionHandle, selectedAnswers);
    } else {
      this.#selections.delete(questionHandle);
    }

    button.setAttribute('aria-pressed', String(!isSelected));
    this.refs.nextButton.disabled = selectedAnswers.size === 0;
    this.#announce(button.textContent?.trim() || '');
    this.#dispatch('inchbug:quiz-selection-change', { selections: this.#selectionDetails() });
  }

  nextStep() {
    const question = this.#quiz.questions[this.#questionIndex];
    if (!question || (this.#selections.get(question.handle)?.size || 0) === 0) return;

    if (this.#questionIndex < this.#quiz.questions.length - 1) {
      this.#showQuestion(this.#questionIndex + 1);
      return;
    }

    if (this.dataset.emailEnabled === 'true') {
      this.#showEmail();
    } else {
      this.#showResults(null, 'email-disabled');
    }
  }

  previousStep() {
    if (!this.refs.emailStage.hidden) {
      this.#showQuestion(this.#quiz.questions.length - 1);
      return;
    }

    if (this.#questionIndex === 0) {
      this.#showIntro();
      return;
    }

    this.#showQuestion(this.#questionIndex - 1);
  }

  /** @param {SubmitEvent} event */
  submitEmail(event) {
    event.preventDefault();
    const email = this.refs.emailInput.value.trim();

    if (email && !this.refs.emailInput.checkValidity()) {
      this.refs.emailInput.reportValidity();
      return;
    }

    if (email) {
      this.#submitOmnisendForm(email);
      this.#syncShopifyCustomer(email);
      this.#trackOmnisendEvent(email);
    }
    this.#emitEmailCapture(email || null, 'submit');
    this.#showResults(email || null, 'submit');
  }

  /**
   * Fire-and-forget POST to the app proxy, which upserts the shopper as a Shopify
   * customer with their quiz tags and recommendation metafields. Runs alongside the
   * Omnisend signup; results never wait on it.
   *
   * @param {string} email
   */
  #syncShopifyCustomer(email) {
    const url = this.dataset.customerSyncUrl;
    if (!url) return;

    const limit = Math.max(1, Number(this.dataset.resultLimit) || 4);
    const products = rankQuizProducts(this.#quiz, this.#selections, limit);

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        quizName: this.dataset.quizName || this.#quiz.handle,
        answers: [...this.#selections.values()].flatMap((answers) => [...answers]),
        productIds: products.map((product) => product.id),
      }),
      keepalive: true,
    }).catch((error) => {
      console.error('[metaobject-product-quiz] Customer sync failed.', error);
    });
  }

  /**
   * Push the shopper's ranked picks to Omnisend as a custom event via the on-site
   * snippet, so an Omnisend automation can render one product card per item.
   *
   * `products` must be an array of objects: only list-type properties can back an
   * Omnisend dynamic content list. The contact is identified first — and awaited —
   * because the JavaScript API only records events for identified contacts, and
   * tracking too early silently files the event under the previous contact.
   *
   * Subscription status and tags are not set here: `identifyContact` accepts only
   * email/phone, and both arrive via Omnisend's Shopify contact sync anyway.
   *
   * @param {string} email
   */
  async #trackOmnisendEvent(email) {
    const eventName = this.dataset.omnisendEvent;
    const omnisend = window.omnisend;
    if (!eventName || !omnisend) return;

    const limit = Math.max(1, Number(this.dataset.resultLimit) || 4);
    const quizName = this.dataset.quizName || this.#quiz.handle;
    const answers = [...this.#selections.values()].flatMap((selected) => [...selected]);
    const products = rankQuizProducts(this.#quiz, this.#selections, limit).map((product) => ({
      title: product.title || product.handle,
      url: withQuizUtm(absoluteUrl(product.url || `/products/${product.handle}`), quizName, product.handle),
      imageUrl: absoluteUrl(product.image),
      price: product.price || '',
    }));

    try {
      const previousId = omnisendContactId();
      omnisend.identifyContact?.({ email });
      await waitForOmnisendContact(previousId);

      omnisend.push([
        'track',
        eventName,
        {
          origin: 'api',
          properties: {
            quizName: this.dataset.quizName || this.#quiz.handle,
            answers,
            products,
          },
        },
      ]);
    } catch (error) {
      console.error('[metaobject-product-quiz] Omnisend event failed.', error);
    }
  }

  /**
   * Fill and submit the hidden Omnisend embedded form. The embed renders
   * asynchronously and shows a success view instead of inputs when Omnisend
   * already knows the visitor, so missing inputs are a silent no-op; results
   * are never blocked on the outcome.
   *
   * @param {string} email
   */
  #submitOmnisendForm(email) {
    const embed = this.refs.omnisendEmbed;
    const emailInput = embed?.querySelector('input[type="email"]');
    if (!(emailInput instanceof HTMLInputElement)) return;

    const limit = Math.max(1, Number(this.dataset.resultLimit) || 4);
    const products = rankQuizProducts(this.#quiz, this.#selections, limit);
    const properties = {
      quiz_name: this.dataset.quizName || this.#quiz.handle,
      quiz_answers: [...this.#selections.values()].flatMap((answers) => [...answers]).join(', '),
      quiz_recommended_products: products
        .map((product) => {
          const url = new URL(product.url || `/products/${product.handle}`, window.location.origin).href;
          return `${product.title || product.handle} – ${url}`;
        })
        .join(' | '),
    };

    this.#setOmnisendValue(emailInput, email);
    for (const [property, value] of Object.entries(properties)) {
      const input = embed.querySelector(`input[name$="${property}"]`);
      if (input instanceof HTMLInputElement) this.#setOmnisendValue(input, value);
    }

    embed.querySelector('button[type="submit"]')?.click();
  }

  /**
   * Omnisend's form is framework-controlled, so use the native value setter
   * plus an input event to make the value stick in its state.
   *
   * @param {HTMLInputElement} input
   * @param {string} value
   */
  #setOmnisendValue(input, value) {
    const nativeValueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    nativeValueSetter ? nativeValueSetter.call(input, value) : (input.value = value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /** @param {MouseEvent} event */
  skipEmail(event) {
    event.preventDefault();
    this.#emitEmailCapture(null, 'skip');
    this.#showResults(null, 'skip');
  }

  restartQuiz() {
    this.#selections.clear();
    this.refs.emailForm.reset();
    this.refs.steps.forEach((step) => {
      step.querySelectorAll('[data-answer-handle]').forEach((answer) => answer.setAttribute('aria-pressed', 'false'));
    });
    this.#showIntro();
  }

  /** @param {boolean} [focus=true] */
  #showIntro(focus = true) {
    this.#hideAllStages();
    this.refs.introStage.hidden = false;
    this.refs.progress.hidden = true;
    if (focus) this.refs.introStage.querySelector('button, a')?.focus({ preventScroll: true });
  }

  /** @param {number} questionIndex */
  #showQuestion(questionIndex) {
    this.#questionIndex = questionIndex;
    this.#hideAllStages();
    this.refs.questionStage.hidden = false;
    this.refs.questionNavigation.hidden = false;
    this.refs.progress.hidden = false;

    this.refs.steps.forEach((step, index) => {
      step.hidden = index !== questionIndex;
    });

    const question = this.#quiz.questions[questionIndex];
    this.refs.nextButton.disabled = !question || (this.#selections.get(question.handle)?.size || 0) === 0;
    this.#updateProgress(questionIndex + 1);
    this.refs.steps[questionIndex]?.querySelector('button')?.focus({ preventScroll: true });
  }

  #showEmail() {
    this.#hideAllStages();
    this.refs.emailStage.hidden = false;
    this.refs.progress.hidden = false;
    this.#updateProgress(this.#quiz.questions.length);
    this.refs.emailInput.focus({ preventScroll: true });
  }

  /**
   * @param {string | null} email
   * @param {string} source
   */
  #showResults(email, source) {
    this.#hideAllStages();
    this.refs.resultsStage.hidden = false;
    this.refs.progress.hidden = true;

    const limit = Math.max(1, Number(this.dataset.resultLimit) || 4);
    const products = rankQuizProducts(this.#quiz, this.#selections, limit);
    this.refs.productCardPool.append(...this.refs.resultsGrid.children);
    this.refs.productCards.forEach((card) => {
      card.hidden = true;
      card.classList.remove('metaobject-product-quiz__result-item');
      card.removeAttribute('data-quiz-rank');
      card.removeAttribute('data-quiz-score');
    });

    const cards = products.map((product, index) => this.#getProductCard(product, index)).filter(Boolean);
    this.refs.resultsGrid.append(...cards);

    if (products.length === 0) {
      const emptyMessage = document.createElement('p');
      emptyMessage.className = 'metaobject-product-quiz__empty';
      emptyMessage.textContent = this.dataset.emptyResults || '';
      this.refs.resultsGrid.append(emptyMessage);
    }

    this.#dispatch('inchbug:quiz-complete', {
      quizHandle: this.#quiz.handle,
      email,
      source,
      selections: this.#selectionDetails(),
      products: products.map(({ id, handle, score }) => ({ id, handle, score })),
    });
    this.refs.resultsStage.querySelector('h2')?.focus({ preventScroll: true });
  }

  /**
   * Move the server-rendered personalized collection card into ranked order.
   *
   * @param {RankedProduct} product
   * @param {number} index
   * @returns {Element | null}
   */
  #getProductCard(product, index) {
    const card = this.refs.productCards.find((candidate) => candidate.dataset.productId === String(product.id));
    if (!(card instanceof HTMLElement)) return null;

    card.hidden = false;
    card.classList.add('metaobject-product-quiz__result-item');
    card.setAttribute('data-quiz-rank', String(index + 1));
    card.setAttribute('data-quiz-score', String(product.score));
    return card;
  }

  #hideAllStages() {
    this.refs.introStage.hidden = true;
    this.refs.questionStage.hidden = true;
    this.refs.questionNavigation.hidden = true;
    this.refs.emailStage.hidden = true;
    this.refs.resultsStage.hidden = true;
  }

  /** @param {number} current */
  #updateProgress(current) {
    const total = this.#quiz.questions.length;
    const percentage = total === 0 ? 0 : Math.round((current / total) * 100);
    const label = (this.dataset.progressLabel || '')
      .replace('__current__', String(current))
      .replace('__total__', String(total));

    this.refs.progressBar.style.setProperty('--quiz-progress', `${percentage}%`);
    this.refs.progress.setAttribute('aria-valuenow', String(percentage));
    this.refs.progressText.textContent = label;
  }

  /**
   * @param {string | null} email
   * @param {string} source
   */
  #emitEmailCapture(email, source) {
    this.#dispatch('inchbug:quiz-email-capture', {
      quizHandle: this.#quiz.handle,
      email,
      source,
      selections: this.#selectionDetails(),
    });
  }

  #selectionDetails() {
    return this.#quiz.questions.map((question) => ({
      questionHandle: question.handle,
      answerHandles: question.answers
        .filter((answer) => this.#selections.get(question.handle)?.has(answer.handle))
        .map((answer) => answer.handle),
    }));
  }

  /**
   * @param {string} name
   * @param {Object} detail
   */
  #dispatch(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  /** @param {string} message */
  #announce(message) {
    this.refs.status.textContent = '';
    requestAnimationFrame(() => {
      this.refs.status.textContent = message;
    });
  }
}

if (!customElements.get('metaobject-product-quiz-component')) {
  customElements.define('metaobject-product-quiz-component', MetaobjectProductQuizComponent);
}
